import React from 'react';
import {
    Show,
    SimpleShowLayout,
    TextField,
    DateField,
    useRecordContext,
    Button,
    useShowController,
    Loading,
    useGetOne,
    useDataProvider,
} from 'react-admin';
import { useParams } from 'react-router-dom';
import { Card, CardContent, Typography, Box, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const JsonSection = ({ title, data }) => {
    if (!data || Object.keys(data).length === 0) return null;

    const renderValue = (value, depth = 0) => {
        if (value === null || value === undefined) return 'N/A';
        
        if (typeof value === 'object') {
            if (Array.isArray(value)) {
                return (
                    <Box sx={{ pl: depth * 2 }}>
                        {value.map((item, index) => (
                            <Box key={index} sx={{ mb: 1 }}>
                                <Typography component="span" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                    [{index}]:
                                </Typography>
                                <Box sx={{ pl: 2, mt: 0.5 }}>
                                    {renderValue(item, depth + 1)}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                );
            } else {
                return (
                    <Box sx={{ pl: depth * 2 }}>
                        {Object.entries(value).map(([key, val]) => (
                            <Box key={key} sx={{ mb: 1 }}>
                                {renderKeyValuePair(key, val, depth + 1)}
                            </Box>
                        ))}
                    </Box>
                );
            }
        }
        
        return (
            <Typography component="span" sx={{ color: 'text.primary' }}>
                {String(value)}
            </Typography>
        );
    };

    const renderKeyValuePair = (key, value, depth = 0) => {
        if (value === null || value === undefined) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                        {key}:
                    </Typography>
                    <Typography sx={{ color: 'text.primary', ml: 1 }}>
                        N/A
                    </Typography>
                </Box>
            );
        }

        // For simple values (strings, numbers, booleans), display inline
        if (typeof value !== 'object' || value === null) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                        {key}:
                    </Typography>
                    <Typography sx={{ color: 'text.primary', ml: 1 }}>
                        {String(value)}
                    </Typography>
                </Box>
            );
        }

        // For objects and arrays, display with proper indentation
        return (
            <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    {key}:
                </Typography>
                <Box sx={{ pl: 2, mt: 0.5 }}>
                    {renderValue(value, depth + 1)}
                </Box>
            </Box>
        );
    };

    const renderTopLevelValue = (value) => {
        if (typeof value === 'object' && value !== null) {
            // Special handling for PWG response structure
            if (value.dane) {
                return (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                            Dane firmy:
                        </Typography>
                        {Object.entries(value.dane).map(([key, val]) => (
                            <Box key={key}>
                                {renderKeyValuePair(key, val)}
                            </Box>
                        ))}
                    </Box>
                );
            }
            return (
                <Box>
                    {Object.entries(value).map(([key, val]) => (
                        <Box key={key}>
                            {renderKeyValuePair(key, val)}
                        </Box>
                    ))}
                </Box>
            );
        }
        return (
            <Typography sx={{ color: 'text.primary' }}>
                {String(value || 'N/A')}
            </Typography>
        );
    };

    return (
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                {Object.entries(data).map(([key, value]) => (
                    <Box key={key} sx={{ mb: 2 }}>
                        {typeof value === 'object' && value !== null ? (
                            <>
                                <Typography sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    {key}
                                </Typography>
                                {renderTopLevelValue(value)}
                            </>
                        ) : (
                            renderKeyValuePair(key, value)
                        )}
                    </Box>
                ))}
            </AccordionDetails>
        </Accordion>
    );
};

const CheckResultField = () => {
    const record = useRecordContext();
    if (!record) return null;
    
    const color = record.result === 'PASS' ? 'success.main' : 'error.main';
    return (
        <Typography variant="h6" sx={{ color }}>
            Result: {record.result}
        </Typography>
    );
};

export const BidderCheckDetails = () => {
    const { bidderId, checkId } = useParams();
    const dataProvider = useDataProvider();
    const [check, setCheck] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        const fetchCheck = async () => {
            try {
                setLoading(true);
                const { data } = await dataProvider.getOne(`bidders/${bidderId}/checks`, {
                    id: checkId,
                });
                setCheck(data);
                setError(null);
            } catch (err) {
                console.error('Error fetching check details:', err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };
        fetchCheck();
    }, [dataProvider, bidderId, checkId]);
    
    if (loading) return <Loading />;
    if (error) return (
        <Box sx={{ p: 2 }}>
            <Typography color="error" variant="h6">
                Error loading check details
            </Typography>
            <Typography color="textSecondary">
                Please try again or contact support if the problem persists.
            </Typography>
        </Box>
    );
    if (!check) return null;

    return (
        <Card>
            <CardContent>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom>
                        Check Details - {check.check_type}
                    </Typography>
                    <Typography variant="h6" sx={{ color: check.result === 'PASS' ? 'success.main' : 'error.main' }}>
                        Result: {check.result}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                            Created: {new Date(check.created_at).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Updated: {new Date(check.updated_at).toLocaleString()}
                        </Typography>
                    </Box>
                </Box>

                <JsonSection title="Request Data" data={check.request_data} />
                <JsonSection title="Response Data" data={check.response_data} />
            </CardContent>
        </Card>
    );
}; 